import React, { useState } from 'react'
import { Badge, Alert, Card, Row, Col, Button, Form, Modal } from 'react-bootstrap'
import { css } from 'emotion'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMapMarkerAlt, faUser, faTruck, faBox, faRoute, faExternalLinkAlt, faClock, faTag, faInfoCircle, faImages, faComments, faFileImage, faCalendarAlt, faEye, faEyeSlash, faPlus, faSpinner, faTrash, faTimes } from '@fortawesome/free-solid-svg-icons'
import moment from 'moment'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { ToastContainer} from "react-toastify";

const DeliveryPointsDisplay = ({ selectedVehicle, selectedVehicles, deliverySpots, loading, onRefreshData, onToggleDelivered }) => {
  const [expandedSpots, setExpandedSpots] = useState({})
  const [showCommentModal, setShowCommentModal] = useState(false)
  const [selectedSpotId, setSelectedSpotId] = useState(null)
  const [isSubmittingComment, setIsSubmittingComment] = useState(false)
  const [commentForm, setCommentForm] = useState({
    deliveryManName: '',
    comment: ''
  })
  const [showMediaModal, setShowMediaModal] = useState(false)
  const [selectedMediaSpotId, setSelectedMediaSpotId] = useState(null)
  const [isSubmittingMedia, setIsSubmittingMedia] = useState(false)
  const [mediaForm, setMediaForm] = useState({
    title: '',
    files: []
  })
  const [showDeleteMediaModal, setShowDeleteMediaModal] = useState(false)
  const [selectedMediaToDelete, setSelectedMediaToDelete] = useState(null)
  const [isDeletingMedia, setIsDeletingMedia] = useState(false)
  const [showDisableModal, setShowDisableModal] = useState(false)
  const [selectedComment, setSelectedComment] = useState(null)
  const [isDisablingComment, setIsDisablingComment] = useState(false)
  
  // Fonction pour basculer l'état d'un spot spécifique
  const toggleSpotExpansion = (spotId) => {
    setExpandedSpots(prev => ({
      ...prev,
      [spotId]: !prev[spotId]
    }))
  }

  // Fonction pour ouvrir le modal d'ajout de commentaire
  const openCommentModal = (spotId) => {
    setSelectedSpotId(spotId)
    setCommentForm({ deliveryManName: '', comment: '' })
    setShowCommentModal(true)
  }

  // Fonction pour fermer le modal
  const closeCommentModal = () => {
    setShowCommentModal(false)
    setSelectedSpotId(null)
    setIsSubmittingComment(false)
    setCommentForm({ deliveryManName: '', comment: '' })
  }

  // Ouvrir le modal d'ajout de média
  const openMediaModal = (spotId) => {
    setSelectedMediaSpotId(spotId)
    setMediaForm({ title: '', files: [] })
    setIsSubmittingMedia(false)
    setShowMediaModal(true)
  }

  // Fermer le modal média
  const closeMediaModal = () => {
    setShowMediaModal(false)
    setSelectedMediaSpotId(null)
    setIsSubmittingMedia(false)
    setMediaForm({ title: '', files: [] })
  }

  const openDeleteMediaModal = (mediaItem, spotId) => {
    const mediaId = mediaItem?.id
    if (!mediaId || !spotId) return
    setSelectedMediaToDelete({
      spotId,
      mediaId,
      title: mediaItem?.title || 'Photo'
    })
    setIsDeletingMedia(false)
    setShowDeleteMediaModal(true)
  }

  const closeDeleteMediaModal = () => {
    setShowDeleteMediaModal(false)
    setSelectedMediaToDelete(null)
    setIsDeletingMedia(false)
  }

  // Fonction pour ouvrir le modal de confirmation de désactivation
  const openDisableModal = (comment, spotId) => {
    setSelectedComment({ ...comment, spotId })
    setShowDisableModal(true)
  }

  // Fonction pour fermer le modal de désactivation
  const closeDisableModal = () => {
    setShowDisableModal(false)
    setSelectedComment(null)
    setIsDisablingComment(false)
  }

  // Fonction pour gérer les changements dans le formulaire
  const handleCommentFormChange = (field, value) => {
    setCommentForm(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleMediaFormChange = (field, value) => {
    setMediaForm(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleMediaFileChange = (event) => {
    const maxSizeMb = 20
    const maxTotalMb = 21
    const maxSizePerFile = maxSizeMb * 1024 * 1024
    const maxTotalSize = maxTotalMb * 1024 * 1024
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
    const selectedFiles = Array.from(event.target.files || [])
    const validFiles = []
    let totalSize = 0

    selectedFiles.forEach((file) => {
      if (file.size > maxSizePerFile) {
        toast.error(`Le fichier ${file.name} dépasse ${maxSizeMb}MB.`)
        return
      }
      if (!allowedTypes.includes(file.type)) {
        toast.error(`Le type de fichier ${file.type} n'est pas autorisé.`)
        return
      }
      totalSize += file.size
      if (totalSize > maxTotalSize) {
        toast.error(`La taille totale dépasse ${maxTotalMb}MB.`)
        return
      }
      validFiles.push(file)
    })

    setMediaForm(prev => ({ ...prev, files: validFiles }))
  }

  // Fonction pour soumettre le commentaire
  const handleCommentSubmit = async () => {
    if (!commentForm.deliveryManName.trim() || !commentForm.comment.trim()) {
      toast.error('Veuillez remplir tous les champs', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      })
      return
    }

    setIsSubmittingComment(true)

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const token = window.localStorage.getItem("token");
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['X-Custom-Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${apiUrl}/api/v2/store/delivery/spots/${selectedSpotId}/comments.json`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          deliveryManName: commentForm.deliveryManName.trim(),
          comment: commentForm.comment.trim()
        })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `Erreur ${response.status}: ${response.statusText}`)
      }

      const newComment = await response.json()
      console.log('Commentaire ajouté avec succès:', newComment)

      // Fermer le modal après soumission
      closeCommentModal()
      
      // Afficher un message de succès
      toast.success('Commentaire ajouté avec succès!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      })
      
      // Recharger les données du véhicule pour afficher le nouveau commentaire
      if (onRefreshData && typeof onRefreshData === 'function') {
        onRefreshData()
      }

    } catch (error) {
      console.error('Erreur lors de l\'ajout du commentaire:', error)
      toast.error(`Erreur lors de l'ajout du commentaire: ${error.message}`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      })
    } finally {
      setIsSubmittingComment(false)
    }
  }

  const handleMediaSubmit = async () => {
    if (!selectedMediaSpotId) return

    if (!mediaForm.files || mediaForm.files.length === 0) {
      toast.error('Veuillez sélectionner au moins une photo')
      return
    }

    setIsSubmittingMedia(true)

    try {
      const apiUrl = process.env.REACT_APP_API_URL
      const token = window.localStorage.getItem("token")

      const headers = {}
      if (token) {
        headers['X-Custom-Authorization'] = `Bearer ${token}`
      }

      const formData = new FormData()
      formData.append('title', (mediaForm.title || '').trim())
      mediaForm.files.forEach((file, index) => {
        formData.append(`medias[${index}]`, file)
      })

      const response = await fetch(`${apiUrl}/api/v2/store/delivery/spots/${selectedMediaSpotId}/medias.json`, {
        method: 'POST',
        headers,
        body: formData
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `Erreur ${response.status}: ${response.statusText}`)
      }

      closeMediaModal()

      toast.success('Photo ajoutée avec succès!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      })

      if (onRefreshData && typeof onRefreshData === 'function') {
        onRefreshData()
      }
    } catch (error) {
      console.error('Erreur lors de l\'ajout du média:', error)
      toast.error(`Erreur lors de l'ajout du média: ${error.message}`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      })
    } finally {
      setIsSubmittingMedia(false)
    }
  }

  const handleDeleteMedia = async () => {
    if (!selectedMediaToDelete?.spotId || !selectedMediaToDelete?.mediaId) return

    setIsDeletingMedia(true)

    try {
      const apiUrl = process.env.REACT_APP_API_URL
      const token = window.localStorage.getItem("token")

      const headers = {}
      if (token) {
        headers['X-Custom-Authorization'] = `Bearer ${token}`
      }

      const response = await fetch(`${apiUrl}/api/v2/store/delivery/spots/${selectedMediaToDelete.spotId}/medias/${selectedMediaToDelete.mediaId}.json`, {
        method: 'DELETE',
        headers
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `Erreur ${response.status}: ${response.statusText}`)
      }

      closeDeleteMediaModal()

      toast.success('Photo supprimée', {
        position: "top-right",
        autoClose: 2500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      })

      if (onRefreshData && typeof onRefreshData === 'function') {
        onRefreshData()
      }
    } catch (error) {
      console.error('Erreur lors de la suppression du média:', error)
      toast.error(`Erreur lors de la suppression du média: ${error.message}`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      })
    } finally {
      setIsDeletingMedia(false)
    }
  }

  // Fonction pour désactiver un commentaire
  const handleDisableComment = async () => {
    if (!selectedComment) return

    setIsDisablingComment(true)

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const token = window.localStorage.getItem("token");
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['X-Custom-Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${apiUrl}/api/v2/store/delivery/spots/${selectedComment.spotId}/comments/${selectedComment.id}.json`, {
        method: 'PUT',
        headers
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `Erreur ${response.status}: ${response.statusText}`)
      }

      console.log('Commentaire désactivé avec succès')

      // Fermer le modal après désactivation
      closeDisableModal()
      
      // Afficher un message de succès
      toast.success('Commentaire désactivé avec succès!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      })
      
      // Recharger les données du véhicule pour masquer le commentaire désactivé
      if (onRefreshData && typeof onRefreshData === 'function') {
        onRefreshData()
      }

    } catch (error) {
      console.error('Erreur lors de la désactivation du commentaire:', error)
      toast.error(`Erreur lors de la désactivation du commentaire: ${error.message}`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      })
    } finally {
      setIsDisablingComment(false)
    }
  }
  
  // Fonction pour obtenir les heures de livraison (priorité aux heures de l'itinéraire)
  const getDeliveryTimes = (spot) => {
    // Priorité aux heures de l'itinéraire (déjà calculées par l'API)
    if (spot.delivery_time && spot.departure_time) {
      return {
        start: spot.delivery_time,
        end: spot.departure_time,
        isDriverTime: true
      }
    }
    
    // Fallback : si un schedule est associé, utiliser ses horaires
    if (spot.schedule) {
      // Priorité aux heures d'arrivée et de départ du livreur du schedule
      if (spot.schedule.deliveryArrivalTime && spot.schedule.deliveryDepartureTime) {
        return {
          start: spot.schedule.deliveryArrivalTime,
          end: spot.schedule.deliveryDepartureTime,
          isDriverTime: true
        }
      }
      
      // Sinon, utiliser les heures de livraison du schedule
      if (spot.schedule.openHourDelivery && spot.schedule.closeHourDelivery) {
        return {
          start: spot.schedule.openHourDelivery,
          end: spot.schedule.closeHourDelivery,
          isDriverTime: false
        }
      }
    }
    
    // Fallback : si les heures d'arrivée et de départ du livreur sont renseignées, les utiliser
    if (spot.delivery_arrival_time && spot.delivery_departure_time) {
      return {
        start: spot.delivery_arrival_time,
        end: spot.delivery_departure_time,
        isDriverTime: true
      }
    }
    
    // Sinon, utiliser les heures de livraison standard
    return {
      start: spot.open_hour_delivery,
      end: spot.close_hour_delivery,
      isDriverTime: false
    }
  }

  // Fonction pour générer un lien d'itinéraire global avec tous les points
  const generateGlobalRouteLink = () => {
    // Filtrer les spots qui ont des coordonnées
    const spotsWithCoordinates = sortedDeliverySpots.filter(spot => spot.lat && spot.lng)
    
    if (spotsWithCoordinates.length === 0) {
      return null
    }

    // Créer l'URL Google Maps avec tous les waypoints
    const waypoints = spotsWithCoordinates.map(spot => `${spot.lat},${spot.lng}`).join('|')
    const googleMapsRouteUrl = `https://www.google.com/maps/dir/?api=1&waypoints=${waypoints}&travelmode=driving&avoid=tolls`

    // Créer l'URL Apple Maps avec tous les waypoints
    // Structure : tous les points en via sauf le dernier en daddr
    // Format : https://maps.apple.com/?via=lat1,lng1&via=lat2,lng2&...&daddr=latN,lngN
    // Les coordonnées numériques n'ont pas besoin d'encodage URL
    let appleMapsRouteUrl
    if (spotsWithCoordinates.length === 1) {
      const { lat, lng } = spotsWithCoordinates[0]
      appleMapsRouteUrl = `https://maps.apple.com/directions?destination=${lat},${lng}&mode=driving&avoid=tolls`
    } else {
      // Apple Maps : pas de multi-waypoints fiable avec avoid=tolls → dernière destination
      const last = spotsWithCoordinates[spotsWithCoordinates.length - 1]
      appleMapsRouteUrl = `https://maps.apple.com/directions?destination=${last.lat},${last.lng}&mode=driving&avoid=tolls`
    }

    return (
      <div className={css(styles.globalRouteContainer)}>
        <Row>
          <Col xs={12} sm={6} className={css(styles.routeButtonCol)}>
            <Button
              variant="success"
              href={googleMapsRouteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={css(styles.globalRouteButton)}
            >
              <FontAwesomeIcon icon={faRoute} className={css(styles.globalRouteIcon)} />
              Google Maps ({spotsWithCoordinates.length} points)
              <FontAwesomeIcon icon={faExternalLinkAlt} className={css(styles.externalIcon)} />
            </Button>
          </Col>
          <Col xs={12} sm={6} className={css(styles.routeButtonCol)}>
            <Button
              variant="secondary"
              href={appleMapsRouteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={css(styles.globalRouteButton)}
            >
              <FontAwesomeIcon icon={faRoute} className={css(styles.globalRouteIcon)} />
              Apple Maps ({spotsWithCoordinates.length} points)
              <FontAwesomeIcon icon={faExternalLinkAlt} className={css(styles.externalIcon)} />
            </Button>
          </Col>
        </Row>
      </div>
    )
  }

  // Fonction pour générer les liens de navigation
  const generateNavigationLinks = (spot) => {
    if (!spot.lat || !spot.lng) {
      return (
        <div className={css(styles.noCoordinates)}>
          <FontAwesomeIcon icon={faMapMarkerAlt} className={css(styles.noCoordinatesIcon)} />
          <span>Coordonnées non disponibles</span>
        </div>
      )
    }

    const address = spot.name || 'Spot de livraison'
    const encodedAddress = encodeURIComponent(address)
    const coordinates = `${spot.lat},${spot.lng}`
    
    const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${coordinates}&travelmode=driving&avoid=tolls`
    const wazeUrl = `https://waze.com/ul?ll=${coordinates}&navigate=yes&avoid_tolls=true`
    const appleMapsUrl = `https://maps.apple.com/directions?destination=${coordinates}&mode=driving&avoid=tolls`
    const googleMapsSearchUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`

    return (
      <div className={css(styles.noPrint)}>
        <hr/>
        <Row style={{ marginTop: '10px' }}>
          <Col xs={4}>
            <a 
              href={googleMapsUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className={css(styles.navigationLink)}
              title="Ouvrir dans Google Maps"
            >
              <FontAwesomeIcon icon={faRoute} className={css(styles.navigationIcon)} />
              <span>GMaps</span>
              <FontAwesomeIcon icon={faExternalLinkAlt} className={css(styles.externalIcon)} />
            </a>
          </Col>
          <Col xs={4}>
            <a 
              href={wazeUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className={css(styles.navigationLink)}
              title="Ouvrir dans Waze"
            >
              <FontAwesomeIcon icon={faRoute} className={css(styles.navigationIcon)} />
              <span>Waze</span>
              <FontAwesomeIcon icon={faExternalLinkAlt} className={css(styles.externalIcon)} />
            </a>
          </Col>
          <Col xs={4}>
            <a 
              href={appleMapsUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className={css(styles.navigationLink)}
              title="Ouvrir dans Apple Plans"
            >
              <FontAwesomeIcon icon={faRoute} className={css(styles.navigationIcon)} />
              <span>Plans</span>
              <FontAwesomeIcon icon={faExternalLinkAlt} className={css(styles.externalIcon)} />
            </a>
          </Col>
        </Row>
        <hr/>
      </div>
    )
  }
  
  // Gestion des cas vides selon le mode
  if (!selectedVehicle) {
    return (
      <div className={css(styles.emptyState)}>
        <FontAwesomeIcon icon={faTruck} className={css(styles.emptyIcon)} />
        <h5>Aucun véhicule sélectionné</h5>
      </div>
    )
  }

  if (loading) {
    return (
      <div className={css(styles.loading)}>
        <FontAwesomeIcon icon={faMapMarkerAlt} spin />
        <span>Chargement des spots de livraison...</span>
      </div>
    )
  }

  if (deliverySpots.length === 0) {
    return (
      <div className={css(styles.emptyState)}>
        <FontAwesomeIcon icon={faMapMarkerAlt} className={css(styles.emptyIcon)} />
        <h5>Aucun spot de livraison</h5>
        <p>Aucun spot de livraison n'est associé à ce véhicule pour le moment.</p>
      </div>
    )
  }

  const apiUrl = process.env.REACT_APP_API_URL;

  const joinUrl = (base, relativePath) => {
    if (!base || !relativePath) return null
    const normalizedBase = base.endsWith('/') ? base.slice(0, -1) : base
    const normalizedRel = relativePath.startsWith('/') ? relativePath.slice(1) : relativePath
    return `${normalizedBase}/${normalizedRel}`
  }

  const getMediaUris = (media) => {
    if (!media) return null
    // L'API expose souvent `mediaUris` (camelCase) ou `media_uris` (snake_case)
    return media.media_uris || media.mediaUris || media.media_uris || null
  }

  const getBestMediaPath = (media, prefer = 'small') => {
    if (!media) return null
    // Ancien format éventuel
    if (media.path) return media.path

    const uris = getMediaUris(media)
    if (!uris) return null

    // Préférer une vignette si dispo, sinon fallback normal/big
    const candidates =
      prefer === 'small'
        ? [uris.small, uris.square, uris.normal, uris.big]
        : [uris.normal, uris.big, uris.small, uris.square]

    return candidates.find(Boolean) || null
  }

  const isLikelyImagePath = (path) => {
    if (!path || typeof path !== 'string') return false
    const clean = path.split('?')[0].toLowerCase()
    return /\.(png|jpe?g|gif|webp|svg)$/.test(clean)
  }

  // Tri des spots par heure de début (les plus tôt en premier)
  const sortedDeliverySpots = [...deliverySpots].sort((a, b) => {
    const deliveryTimesA = getDeliveryTimes(a)
    const deliveryTimesB = getDeliveryTimes(b)
    
    // Si les deux spots ont une heure de début
    if (deliveryTimesA.start && deliveryTimesB.start) {
      return moment(deliveryTimesA.start).diff(moment(deliveryTimesB.start))
    }
    // Si seul le premier spot a une heure, il passe en premier
    if (deliveryTimesA.start && !deliveryTimesB.start) {
      return -1
    }
    // Si seul le deuxième spot a une heure, il passe en premier
    if (!deliveryTimesA.start && deliveryTimesB.start) {
      return 1
    }
    // Si aucun des deux n'a d'heure, on garde l'ordre original
    return 0
  })

  return (
    <div className={css(styles.container)}>
      {/* Bouton d'itinéraire global */}
      {generateGlobalRouteLink()}
      
      {/* Spots de livraison en cartes */}
      <ToastContainer
            stacked
            limit={1}
            position="bottom-right"
            autoClose={4000}
            hideProgressBar={true}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnVisibilityChange
            draggable={false}
            pauseOnHover
            className='no-print'
          />
      <div className={css(styles.cardsContainer)}>
        <Row>
          
          {sortedDeliverySpots.map((spot, index) => (
            <Col 
              key={spot.id || index} 
              xs={12} 
              sm={12} 
              lg={12} 
              className={css(styles.cardColumn)}
            >
              <Card className={css(styles.deliveryCard)}>
                <Card.Body>
                  {/* En-tête de la carte */}
                  {spot.deposit && (
                    <Badge variant="info" className={css(styles.typeBadge)}>
                      Dépôt
                    </Badge>
                  )}
                  {spot.for_company && (
                    <Badge variant="secondary" className={css(styles.typeBadge)}>
                      Cantine
                    </Badge>
                  )}
                  {!spot.private && !spot.deposit && !spot.for_company && (
                    <Badge variant="success" className={css(styles.typeBadge)}>
                      Public
                    </Badge>
                  )}
                  {spot.delivered && (
                    <Badge variant="success" className={css(styles.typeBadge, styles.deliveredBadge)}>
                      ✓ Livré{spot.delivered_at ? ` à ${moment(spot.delivered_at).format('HH:mm')}` : ''}
                    </Badge>
                  )}

                  <div className={css(styles.cardHeader, styles.cardHeaderRow)}>
                    <div className={css(styles.spotTitle)}>
                      <span className={css(styles.spotName)}>
                        <big>{spot.number_spot}</big> - {spot.name || 'Spot de livraison'}
                      </span>
                    </div>
                    {onToggleDelivered && (
                      <Button
                        variant={spot.delivered ? 'success' : 'outline-success'}
                        size="sm"
                        className={css(styles.deliveredButton)}
                        title={spot.delivered
                          ? `Livré${spot.delivered_at ? ` à ${moment(spot.delivered_at).format('HH:mm')}` : ''} — cliquer pour annuler`
                          : 'Marquer ce point comme livré'}
                        onClick={() => onToggleDelivered(spot)}
                      >
                        {spot.delivered
                          ? `✓ Livré${spot.delivered_at ? ` à ${moment(spot.delivered_at).format('HH:mm')}` : ''}`
                          : 'Marquer livré'}
                      </Button>
                    )}
                  </div>
                  {/* Description */}
                  {spot.descr && (
                    <div className={css(styles.descriptionSection)}>
                      <span className={css(styles.spotInstructions)}>
                        {spot.descr}
                      </span>
                    </div>
                  )}

                  {/* Horaires */}
                  <div className={css(styles.scheduleSection)} style={{ marginTop: '10px' }}>
                    <FontAwesomeIcon icon={faClock} className={css(styles.sectionIcon)} style={{ marginRight: '5px' }} /> 
                    {(() => {
                      const deliveryTimes = getDeliveryTimes(spot)
                      if (deliveryTimes.start && deliveryTimes.end) {
                        const startTime = moment(deliveryTimes.start).format('HH:mm')
                        const endTime = moment(deliveryTimes.end).format('HH:mm')
                        return `${startTime} - ${endTime}`
                      }
                      return 'Non défini'
                    })()}
                  </div>

                  {/* Adresse : calculée depuis les coordonnées (numéro le plus proche) ou saisie au BO */}
                  {spot.full_address && (
                    <div className={css(styles.scheduleSection)} style={{ marginTop: '6px' }}>
                      <FontAwesomeIcon icon={faMapMarkerAlt} className={css(styles.sectionIcon)} style={{ marginRight: '5px' }} />
                      {spot.full_address_auto ? 'Adresse approximative' : 'Adresse'} : <span className={css(styles.spotAddress)}>{spot.full_address}</span>
                    </div>
                  )}
                  
                  {/* Informations du schedule */}
                  {spot.schedule && (
                    <div className={css(styles.scheduleInfo)} style={{ marginTop: '8px', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                      <div style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                        <strong>Schedule:</strong> {spot.schedule.numberSpot || 'N/A'}
                      </div>
                      {spot.schedule.days && spot.schedule.days.length > 0 && (
                        <div style={{ fontSize: '0.8rem', color: '#6c757d', marginTop: '2px' }}>
                          Jours: {spot.schedule.days.map(day => {
                            const dayNames = ['', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']
                            return dayNames[day] || day
                          }).join(', ')}
                        </div>
                      )}
                    </div>
                  )}
                  {spot.more_information && (
                    <div className={css(styles.printSection)} style={{ marginTop: '10px' }}>
                      <h6 className={css(styles.printSectionTitle)}>
                        <FontAwesomeIcon icon={faInfoCircle} className={css(styles.printIcon)} style={{ marginRight: '5px' }} />Info supp :
                      </h6>
                      <div 
                        className={css(styles.printContent)}
                        dangerouslySetInnerHTML={{ __html: spot.more_information }}
                      />
                    </div>
                  )}
                  {/* Navigation */}
                  <div className={css(styles.navigationSection)}>
                    {generateNavigationLinks(spot)}
                  </div>

                  {/* Boutons d'action pour ce spot */}
                  <div className={css(styles.spotActionsContainer)}>
                    {(spot.delivery_spot_comments && spot.delivery_spot_comments.length > 0) || (spot.delivery_spot_medias && spot.delivery_spot_medias.length > 0) ? (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => toggleSpotExpansion(spot.id || index)}
                        className={css(styles.spotToggleButton)}
                      >
                        <FontAwesomeIcon 
                          icon={expandedSpots[spot.id || index] ? faEyeSlash : faEye} 
                          className={css(styles.spotToggleIcon)} 
                        />
                        {expandedSpots[spot.id || index] ? 'Masquer' : 'Voir'} commentaires et médias
                      </Button>
                    ) : null}
                    
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => openCommentModal(spot.id || index)}
                      className={css(styles.addCommentButton)}
                    >
                      <FontAwesomeIcon icon={faPlus} className={css(styles.addCommentIcon)} />
                      Ajouter un commentaire
                    </Button>

                    <Button
                      variant="outline-success"
                      size="sm"
                      onClick={() => openMediaModal(spot.id || index)}
                      className={css(styles.addMediaButton)}
                    >
                      <FontAwesomeIcon icon={faFileImage} className={css(styles.addMediaIcon)} />
                      Ajouter une photo
                    </Button>
                  </div>

                  {spot.delivery_spot_comments && spot.delivery_spot_comments.length > 0 && (
                    <div className={css(styles.printSection, expandedSpots[spot.id || index] ? styles.showContent : styles.hideContent)} style={{ marginTop: '10px' }}>
                      <h6 className={css(styles.printSectionTitle)}>
                        <FontAwesomeIcon icon={faComments} className={css(styles.printIcon)} style={{ marginRight: '5px' }} />
                        Commentaires ({spot.delivery_spot_comments.length})
                      </h6>
                      <div className={css(styles.postItContainer)}>
                        {spot.delivery_spot_comments && spot.delivery_spot_comments.map((comment, commentIndex) => (
                          <div key={commentIndex} className={css(styles.postIt)}>
                            <div className={css(styles.postItHeader)}>
                              <FontAwesomeIcon icon={faUser} className={css(styles.postItUserIcon)} />
                              <strong className={css(styles.postItAuthor)}>{comment.delivery_man_name}</strong>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => openDisableModal(comment, spot.id || index)}
                                className={css(styles.disableCommentButton)}
                                title="Désactiver ce commentaire"
                              >
                                <FontAwesomeIcon icon={faTrash} className={css(styles.disableCommentIcon)} />
                              </Button>
                            </div>
                            <div className={css(styles.postItDate)}>
                              {moment(comment.created_at).format('DD/MM/YYYY HH:mm')}
                            </div>
                            <div className={css(styles.postItContent)}>
                              {comment.comment}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Détails */}
                  {spot.delivery_spot_medias && spot.delivery_spot_medias.length > 0 && (
                    <div className={css(styles.printSection, expandedSpots[spot.id || index] ? styles.showContent : styles.hideContent)} style={{ marginTop: '10px' }}>
                      <h6 className={css(styles.printSectionTitle)}>
                        <FontAwesomeIcon icon={faImages} className={css(styles.printIcon)} />
                        Médias ({spot.delivery_spot_medias && spot.delivery_spot_medias.length})
                      </h6>
                      <div className={css(styles.printMediasHorizontal)}>
                        {spot.delivery_spot_medias.map((mediaItem, index) => (
                          <div key={index} className={css(styles.printMediaItemHorizontal)}>
                            {(() => {
                              const fullPath = getBestMediaPath(mediaItem?.media, 'normal')
                              const thumbPath = getBestMediaPath(mediaItem?.media, 'small') || fullPath
                              const fullUrl = joinUrl(apiUrl, fullPath)
                              const thumbUrl = joinUrl(apiUrl, thumbPath)
                              const title = mediaItem?.title || 'Media'

                              if (!fullUrl) return null

                              const isImage = isLikelyImagePath(fullPath) || isLikelyImagePath(thumbPath)

                              return (
                                <div className={css(styles.printMediaUrl)}>
                                  {title && (
                                    <div className={css(styles.printMediaTitle)}>
                                      {title}
                                    </div>
                                  )}
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      openDeleteMediaModal(mediaItem, spot.id || index)
                                    }}
                                    className={css(styles.deleteMediaButton)}
                                    title="Supprimer cette photo"
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                  </Button>
                                  <a
                                    href={fullUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={css(styles.imageLink)}
                                    title="Cliquer pour ouvrir"
                                  >
                                    {isImage ? (
                                      <img
                                        src={thumbUrl || fullUrl}
                                        alt={title}
                                        className={css(styles.printMediaImage)}
                                        style={{ maxWidth: '200px' }}
                                      />
                                    ) : (
                                      <span className={css(styles.printMediaFileLink)}>
                                        <FontAwesomeIcon icon={faFileImage} style={{ marginRight: '6px' }} />
                                        Ouvrir le fichier
                                      </span>
                                    )}
                                  </a>
                                </div>
                              )
                            })()}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      {/* Modal pour ajouter un commentaire */}
      <Modal show={showCommentModal} onHide={closeCommentModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faComments} className={css(styles.modalIcon)} />
            Ajouter un commentaire
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Nom du livreur *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Entrez le nom du livreur"
                value={commentForm.deliveryManName}
                onChange={(e) => handleCommentFormChange('deliveryManName', e.target.value)}
                className={css(styles.formControl)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Commentaire *</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Entrez votre commentaire..."
                value={commentForm.comment}
                onChange={(e) => handleCommentFormChange('comment', e.target.value)}
                className={css(styles.formControl)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={closeCommentModal}
            disabled={isSubmittingComment}
          >
            Annuler
          </Button>
          <Button 
            variant="primary" 
            onClick={handleCommentSubmit}
            disabled={isSubmittingComment}
            className={css(styles.submitButton)}
          >
            {isSubmittingComment ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className={css(styles.submitIcon)} />
                Envoi en cours...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faPlus} className={css(styles.submitIcon)} />
                Ajouter le commentaire
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal pour ajouter une photo */}
      <Modal show={showMediaModal} onHide={closeMediaModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faFileImage} className={css(styles.modalIcon)} />
            Ajouter une photo
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Titre (optionnel)</Form.Label>
              <Form.Control
                type="text"
                placeholder="Ex: Entrée, parking, code porte…"
                value={mediaForm.title}
                onChange={(e) => handleMediaFormChange('title', e.target.value)}
                className={css(styles.formControl)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Photo *</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                multiple
                onChange={handleMediaFileChange}
                className={css(styles.formControl)}
              />
              <div style={{ fontSize: '0.8rem', color: '#6c757d', marginTop: '6px' }}>
                Formats: JPG/PNG/GIF/WEBP — max 20MB par fichier, 21MB au total
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeMediaModal}
            disabled={isSubmittingMedia}
          >
            Annuler
          </Button>
          <Button
            variant="success"
            onClick={handleMediaSubmit}
            disabled={isSubmittingMedia}
            className={css(styles.submitButton)}
          >
            {isSubmittingMedia ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className={css(styles.submitIcon)} />
                Envoi en cours...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faFileImage} className={css(styles.submitIcon)} />
                Ajouter la photo
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de confirmation pour supprimer un média */}
      <Modal show={showDeleteMediaModal} onHide={closeDeleteMediaModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faTrash} className={css(styles.modalIcon)} />
            Supprimer la photo
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Êtes-vous sûr de vouloir supprimer cette photo ?</p>
          {selectedMediaToDelete && (
            <div className={css(styles.commentPreview)}>
              <div className={css(styles.commentPreviewHeader)}>
                <FontAwesomeIcon icon={faFileImage} className={css(styles.commentPreviewIcon)} />
                <strong>{selectedMediaToDelete.title}</strong>
              </div>
              <div className={css(styles.commentPreviewContent)}>
                Cette action est irréversible.
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeDeleteMediaModal}
            disabled={isDeletingMedia}
          >
            Annuler
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteMedia}
            disabled={isDeletingMedia}
            className={css(styles.disableButton)}
          >
            {isDeletingMedia ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className={css(styles.submitIcon)} />
                Suppression...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faTrash} className={css(styles.submitIcon)} />
                Supprimer
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de confirmation pour désactiver un commentaire */}
      <Modal show={showDisableModal} onHide={closeDisableModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faTrash} className={css(styles.modalIcon)} />
            Désactiver le commentaire
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Êtes-vous sûr de vouloir désactiver ce commentaire ?</p>
          {selectedComment && (
            <div className={css(styles.commentPreview)}>
              <div className={css(styles.commentPreviewHeader)}>
                <FontAwesomeIcon icon={faUser} className={css(styles.commentPreviewIcon)} />
                <strong>{selectedComment.delivery_man_name}</strong>
                <span className={css(styles.commentPreviewDate)}>
                  {moment(selectedComment.created_at).format('DD/MM/YYYY HH:mm')}
                </span>
              </div>
              <div className={css(styles.commentPreviewContent)}>
                {selectedComment.comment}
              </div>
            </div>
          )}
          <p className={css(styles.warningText)}>
            <FontAwesomeIcon icon={faInfoCircle} className={css(styles.warningIcon)} />
            Cette action masquera le commentaire de l'affichage. Il ne sera plus visible dans la liste des commentaires.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={closeDisableModal}
            disabled={isDisablingComment}
          >
            Annuler
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDisableComment}
            disabled={isDisablingComment}
            className={css(styles.disableButton)}
          >
            {isDisablingComment ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className={css(styles.submitIcon)} />
                Désactivation...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faTrash} className={css(styles.submitIcon)} />
                Désactiver le commentaire
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  )
}

const styles = {
  container: {
    padding: '0'
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    color: '#6c757d'
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#6c757d'
  },
  emptyIcon: {
    fontSize: '3rem',
    marginBottom: '20px',
    opacity: 0.5,
    color: '#dee2e6'
  },
  spotName: {
    fontWeight: 'bold',
    color: '#495057'
  },
  spotInstructions: {
    fontSize: '0.85rem',
    color: '#6c757d',
    marginTop: '2px',
    fontStyle: 'italic'
  },
  typeBadge: {
    fontSize: '0.75rem',
    marginRight: '2px',
    marginBottom: '2px'
  },
  // Ligne titre du point + bouton livré : le bouton se cale à droite
  cardHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  // Badge « ✓ Livré » : visible aussi à l'impression (état au moment de l'impression)
  deliveredBadge: {
    backgroundColor: '#28a745',
    color: '#fff'
  },
  // Bouton de marquage livré : action écran uniquement, masqué à l'impression
  deliveredButton: {
    marginLeft: 'auto',
    flexShrink: 0,
    '@media print': {
      display: 'none !important'
    }
  },
  navigationLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 8px',
    backgroundColor: '#f8f9fa',
    border: '1px solid #dee2e6',
    borderRadius: '4px',
    textDecoration: 'none',
    color: '#495057',
    fontSize: '0.8rem',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#e9ecef',
      borderColor: '#adb5bd',
      textDecoration: 'none',
      color: '#495057'
    }
  },
  navigationIcon: {
    fontSize: '0.7rem',
    color: '#007bff'
  },
  externalIcon: {
    fontSize: '0.6rem',
    color: '#6c757d',
    marginLeft: 'auto'
  },
  noCoordinates: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    fontSize: '0.8rem',
    color: '#6c757d',
    fontStyle: 'italic'
  },
  noCoordinatesIcon: {
    fontSize: '0.7rem',
    color: '#6c757d'
  },
  // Styles pour l'impression
  '@media print': {
    container: {
      pageBreakInside: 'avoid'
    }
  },
  noPrint: {
    '@media print': {
      display: 'none !important'
    }
  },
  // Styles pour les boutons d'action du spot
  spotActionsContainer: {
    marginTop: '15px',
    marginBottom: '10px',
    display: 'flex',
    justifyContent: 'center',
    gap: '10px',
    flexWrap: 'wrap',
    '@media print': {
      display: 'none !important'
    }
  },
  spotToggleButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    fontSize: '0.8rem',
    borderRadius: '4px',
    backgroundColor: '#fff',
    color: '#6c757d',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#f8f9fa',
      borderColor: '#495057',
      color: '#495057'
    },
    '&:focus': {
      boxShadow: '0 0 0 0.2rem rgba(108, 117, 125, 0.25)'
    }
  },
  spotToggleIcon: {
    fontSize: '0.7rem'
  },
  // Styles pour l'affichage conditionnel
  showContent: {
    display: 'block'
  },
  hideContent: {
    display: 'none',
    '@media print': {
      display: 'block !important'
    }
  },
  // Styles pour l'affichage horizontal des médias
  printMediasHorizontal: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    alignItems: 'flex-start'
  },
  printMediaItemHorizontal: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '10px'
  },
  printMediaUrl: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
    position: 'relative'
  },
  deleteMediaButton: {
    position: 'absolute',
    top: '0px',
    right: '0px',
    padding: '4px 6px',
    fontSize: '0.75rem',
    borderRadius: '6px',
    backgroundColor: 'rgba(255,255,255,0.9)',
    '@media print': {
      display: 'none !important'
    }
  },
  printMediaTitle: {
    fontSize: '0.8rem',
    fontWeight: '600',
    color: '#495057',
    textAlign: 'center',
    maxWidth: '220px',
    wordBreak: 'break-word'
  },
  printMediaImage: {
    borderRadius: '6px',
    border: '1px solid #dee2e6',
    boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
    maxHeight: '140px',
    objectFit: 'cover'
  },
  printMediaFileLink: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '6px 10px',
    fontSize: '0.85rem',
    borderRadius: '6px',
    border: '1px solid #dee2e6',
    backgroundColor: '#f8f9fa',
    color: '#495057'
  },
  imageLink: {
    display: 'inline-block',
    textDecoration: 'none',
    transition: 'opacity 0.2s ease',
    '&:hover': {
      opacity: 0.8
    },
    '&:focus': {
      outline: '2px solid #007bff',
      outlineOffset: '2px'
    }
  },
  addCommentButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    fontSize: '0.8rem',
    borderRadius: '4px',
    backgroundColor: '#fff',
    color: '#007bff',
    border: '1px solid #007bff',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#007bff',
      color: '#fff'
    },
    '&:focus': {
      boxShadow: '0 0 0 0.2rem rgba(0, 123, 255, 0.25)'
    },
    '@media print': {
      display: 'none !important'
    }
  },
  addCommentIcon: {
    fontSize: '0.7rem'
  },
  addMediaButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    fontSize: '0.8rem',
    borderRadius: '4px',
    backgroundColor: '#fff',
    color: '#28a745',
    border: '1px solid #28a745',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#28a745',
      color: '#fff'
    },
    '&:focus': {
      boxShadow: '0 0 0 0.2rem rgba(40, 167, 69, 0.25)'
    },
    '@media print': {
      display: 'none !important'
    }
  },
  addMediaIcon: {
    fontSize: '0.75rem'
  },
  // Styles pour le modal
  modalIcon: {
    marginRight: '8px',
    color: '#007bff'
  },
  formControl: {
    border: '1px solid #ced4da',
    borderRadius: '4px',
    padding: '8px 12px',
    fontSize: '0.9rem',
    transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
    '&:focus': {
      borderColor: '#007bff',
      boxShadow: '0 0 0 0.2rem rgba(0, 123, 255, 0.25)',
      outline: 'none'
    }
  },
  submitIcon: {
    marginRight: '6px',
    fontSize: '0.8rem'
  },
  submitButton: {
    position: 'relative',
    '&:disabled': {
      opacity: 0.7,
      cursor: 'not-allowed'
    }
  },
  // Styles pour les post-it
  postItContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '15px',
    marginTop: '10px',
    alignItems: 'flex-start'
  },
  postIt: {
    backgroundColor: '#fffacd', // Jaune post-it
    border: '1px solid #f0e68c',
    borderRadius: '8px',
    padding: '12px',
    minWidth: '200px',
    maxWidth: '300px',
    flex: '1 1 auto',
    boxShadow: '2px 2px 8px rgba(0,0,0,0.1)',
    position: 'relative',
    transform: 'rotate(-1deg)',
    transition: 'transform 0.2s ease',
    paddingTop: '40px', // Espace pour le bouton de désactivation
    '&:hover': {
      transform: 'rotate(0deg) scale(1.02)',
      boxShadow: '3px 3px 12px rgba(0,0,0,0.15)'
    },
    '&:nth-child(even)': {
      backgroundColor: '#f0f8ff', // Bleu clair pour alternance
      borderColor: '#b0d4f1',
      transform: 'rotate(1deg)',
      '&:hover': {
        transform: 'rotate(0deg) scale(1.02)'
      }
    },
    '&:nth-child(3n)': {
      backgroundColor: '#f5f5dc', // Beige pour variété
      borderColor: '#ddd8b8',
      transform: 'rotate(-0.5deg)',
      '&:hover': {
        transform: 'rotate(0deg) scale(1.02)'
      }
    }
  },
  postItHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '8px',
    borderBottom: '1px solid rgba(0,0,0,0.1)',
    paddingBottom: '6px'
  },
  postItUserIcon: {
    fontSize: '0.8rem',
    color: '#666',
    marginRight: '6px'
  },
  postItAuthor: {
    fontSize: '0.85rem',
    color: '#333',
    margin: 0
  },
  postItDate: {
    fontSize: '0.7rem',
    color: '#666',
    fontStyle: 'italic',
    marginBottom: '8px'
  },
  postItContent: {
    fontSize: '0.85rem',
    color: '#444',
    lineHeight: '1.4',
    wordWrap: 'break-word'
  },
  // Styles pour le bouton de désactivation
  disableCommentButton: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    padding: '4px 6px',
    fontSize: '0.7rem',
    borderRadius: '3px',
    backgroundColor: 'transparent',
    border: '1px solid #dc3545',
    color: '#dc3545',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#dc3545',
      color: '#fff',
      borderColor: '#dc3545'
    },
    '&:focus': {
      boxShadow: '0 0 0 0.2rem rgba(220, 53, 69, 0.25)'
    },
    '@media print': {
      display: 'none !important'
    }
  },
  disableCommentIcon: {
    fontSize: '0.6rem'
  },
  // Styles pour la modal de désactivation
  commentPreview: {
    backgroundColor: '#f8f9fa',
    border: '1px solid #dee2e6',
    borderRadius: '6px',
    padding: '12px',
    margin: '15px 0'
  },
  commentPreviewHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '8px',
    borderBottom: '1px solid #dee2e6',
    paddingBottom: '6px'
  },
  commentPreviewIcon: {
    fontSize: '0.8rem',
    color: '#666',
    marginRight: '6px'
  },
  commentPreviewDate: {
    fontSize: '0.7rem',
    color: '#666',
    fontStyle: 'italic',
    marginLeft: 'auto'
  },
  commentPreviewContent: {
    fontSize: '0.85rem',
    color: '#444',
    lineHeight: '1.4',
    wordWrap: 'break-word'
  },
  warningText: {
    fontSize: '0.85rem',
    color: '#856404',
    backgroundColor: '#fff3cd',
    border: '1px solid #ffeaa7',
    borderRadius: '4px',
    padding: '10px',
    margin: '15px 0 0 0',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px'
  },
  warningIcon: {
    fontSize: '0.8rem',
    color: '#856404',
    marginTop: '2px',
    flexShrink: 0
  },
  disableButton: {
    position: 'relative',
    '&:disabled': {
      opacity: 0.7,
      cursor: 'not-allowed'
    }
  },
  // Styles pour le bouton d'itinéraire global
  globalRouteContainer: {
    marginBottom: '20px',
    textAlign: 'left',
    '@media print': {
      display: 'none !important'
    }
  },
  globalRouteIcon: {
    fontSize: '1.1rem'
  },
  routeButtonCol: {
    marginBottom: '10px'
  },
  globalRouteButton: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
  },
  scheduleInfo: {
    border: '1px solid #dee2e6',
    fontSize: '0.85rem'
  }
}

export default DeliveryPointsDisplay
